'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Instagram, Facebook, Youtube } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { buildLocalePath, type Locale } from '@/i18n/config';
import { useCookieConsentReset } from '@/components/consent/CookieConsentBanner';
import { useRegional } from '@/components/providers/regional-provider';

export interface FooterLink {
  label: string;
  href: string;
  target?: string;
  rel?: string;
}

export interface FooterSection {
  title: string;
  links: FooterLink[];
}

export interface PublicFooterProps {
  tenantName?: string;
  sections?: FooterSection[];
  copyright?: string;
}

// Supporter logos
const supporterLogos = [
  { name: 'ELF', src: '/images/dgr/sponsors/ELF_Logo.svg', href: 'https://www.elf.com' },
  { name: 'Hedon', src: '/images/dgr/sponsors/Hedon_Logo.svg', href: 'https://www.hedon.com' },
  {
    name: 'Quad Lock',
    src: '/images/dgr/sponsors/QL_Logo.svg',
    href: 'https://www.quadlockcase.com',
  },
  {
    name: 'Brett',
    src: '/images/dgr/sponsors/Brett_Logo_White.svg',
    href: 'https://www.brett-eyewear.com',
  },
  {
    name: 'Bulleit',
    src: '/images/dgr/sponsors/Bulleit_Logo.svg',
    href: 'https://www.bulleit.com',
    countryOnly: 'US',
  },
];

// Footer sections - labels use translation keys, resolved at render time
// Each label corresponds to a nav.* key in the i18n messages
interface FooterSectionConfig {
  titleKey: string; // footer.* key for section title
  links: { labelKey: string; href: string; target?: string; rel?: string }[];
}

const defaultSectionConfigs: FooterSectionConfig[] = [
  {
    titleKey: 'theRide',
    links: [
      { labelKey: 'aboutDgr', href: '/about' },
      { labelKey: 'dapperGuide', href: '/about/dapper-guide' },
      { labelKey: 'styleGuide', href: '/about/style-guide' },
      { labelKey: 'mensHealth', href: '/about/mens-health' },
      { labelKey: 'programsWeFund', href: '/about/programs-we-fund' },
    ],
  },
  {
    titleKey: 'event',
    links: [
      { labelKey: 'findARide', href: '/events' },
      { labelKey: 'hostARide', href: '/about/host-a-ride' },
      { labelKey: 'globalMap', href: '/map' },
      { labelKey: 'register', href: '/register' },
      { labelKey: 'leaderboards', href: '/leaderboard' },
    ],
  },
  {
    titleKey: 'partners',
    links: [
      { labelKey: 'triumphMotorcycles', href: '/about/triumphmotorcycles' },
      { labelKey: 'eventPartners', href: '/about/partners' },
      { labelKey: 'featuredTeams', href: '/partner-teams' },
      { labelKey: 'sponsorARide', href: '/sponsor' },
    ],
  },
  {
    titleKey: 'support',
    links: [
      { labelKey: 'contactUs', href: '/contact' },
      { labelKey: 'faqs', href: '/about/faq' },
      { labelKey: 'safetyFaq', href: '/about/safety-information' },
      { labelKey: 'pressMedia', href: '/press' },
      { labelKey: 'downloads', href: '/about/downloads' },
    ],
  },
  {
    titleKey: 'community',
    links: [
      { labelKey: 'blog', href: '/blog' },
      { labelKey: 'gallery', href: '/gallery' },
      { labelKey: 'uploadPhotos', href: '/upload' },
      {
        labelKey: 'shop',
        href: 'https://shop.gentlemansride.com',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      { labelKey: 'rewards', href: '/rewards' },
    ],
  },
  {
    titleKey: 'legal',
    links: [
      { labelKey: 'privacyPolicy', href: '/privacy' },
      { labelKey: 'termsOfService', href: '/terms' },
    ],
  },
];

export function PublicFooter({
  tenantName = "The Distinguished Gentleman's Ride",
  sections: sectionsProp,
  copyright,
}: PublicFooterProps) {
  const locale = useLocale() as Locale;
  const t = useTranslations('footer');
  const tNav = useTranslations('nav');
  const tCommon = useTranslations('common');
  const currentYear = new Date().getFullYear();
  const resetCookieConsent = useCookieConsentReset();
  const { detectedCountry } = useRegional();

  const filteredSupporterLogos = supporterLogos.filter(
    (logo) => !logo.countryOnly || logo.countryOnly === detectedCountry,
  );

  // Build translated sections from config if no custom sections provided
  const sections: FooterSection[] =
    sectionsProp ??
    defaultSectionConfigs.map((config) => ({
      title: t(config.titleKey as 'theRide'),
      links: config.links.map((link) => ({
        label: tNav(link.labelKey as 'aboutDgr'),
        href: link.href.startsWith('#') ? link.href : buildLocalePath(locale, link.href),
        target: link.target,
        rel: link.rel,
      })),
    }));

  return (
    <footer className="bg-stone-950 text-white">
      {/* Partners Bar */}
      <div className="border-b border-white/10">
        <div className="container py-6">
          {/* Main Partners */}
          <div className="flex items-center justify-center gap-8 mb-10 mt-6">
            {/* Mobile: just logos side by side */}
            <div className="flex sm:hidden items-center justify-center gap-6">
              <Link
                prefetch={false}
                href="https://movember.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src="/images/dgr/sponsors/MovemberLogo.svg"
                  alt={tCommon('movemberAlt')}
                  width={120}
                  height={28}
                  className="h-6 w-auto opacity-100"
                />
              </Link>
              <div className="w-px h-6 bg-white/20" />
              <Link
                prefetch={false}
                href="https://triumph.co.uk"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src="/images/dgr/sponsors/TriumphLogo.svg"
                  alt={tCommon('triumphAlt')}
                  width={138}
                  height={40}
                  className="h-10 w-auto opacity-100"
                />
              </Link>
            </div>
            {/* Desktop: with labels above */}
            <div className="hidden sm:flex flex-col items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-white/40">
                {t('supporting')}
              </span>
              <Link
                prefetch={false}
                href="https://movember.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src="/images/dgr/sponsors/MovemberLogo.svg"
                  alt={tCommon('movemberAlt')}
                  width={140}
                  height={32}
                  className="h-7 w-auto opacity-100 hover:opacity-100 transition-opacity"
                />
              </Link>
            </div>
            <div className="hidden sm:flex flex-col items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-white/40">
                {t('sponsoredBy')}
              </span>
              <Link
                prefetch={false}
                href="https://triumph.co.uk"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src="/images/dgr/sponsors/TriumphLogo.svg"
                  alt={tCommon('triumphAlt')}
                  width={150}
                  height={32}
                  className="h-8 w-auto opacity-100 hover:opacity-100 transition-opacity"
                />
              </Link>
            </div>
          </div>
          {/* Supporters */}
          <div className="flex items-center justify-center gap-4 sm:gap-8 my-6 flex-wrap">
            {filteredSupporterLogos.map((logo) => (
              <a key={logo.name} href={logo.href} target="_blank" rel="noopener noreferrer">
                <Image
                  src={logo.src}
                  alt={logo.name}
                  width={100}
                  height={28}
                  className="h-5 sm:h-6 w-auto opacity-100 hover:opacity-80 transition-opacity"
                />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Follow Us / Get The App */}
      <div className="border-b border-white/10">
        <div className="container py-6">
          <div className="flex flex-col items-center gap-8 sm:flex-row sm:items-start sm:justify-center sm:gap-24 lg:gap-32">
            <div className="flex flex-col items-center">
              <h3 className="text-[10px] uppercase tracking-widest text-white/40 text-center mb-5">
                {t('followUs')}
              </h3>
              <div className="flex items-center justify-center gap-5 h-10">
                <a
                  href="https://instagram.com/gentlemansride"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-white transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-6 h-6" />
                </a>
                <a
                  href="https://facebook.com/gentlemansride"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-white transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-6 h-6" />
                </a>
                <a
                  href="https://youtube.com/@DistinguishedGentlemansRide"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-white transition-colors"
                  aria-label="YouTube"
                >
                  <Youtube className="w-6 h-6" />
                </a>
                <a
                  href="https://x.com/gentlemansride"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-white transition-colors"
                  aria-label="X"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a
                  href="https://www.tiktok.com/@gentlemansride"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-white transition-colors"
                  aria-label="TikTok"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V9.4a8.16 8.16 0 0 0 4.77 1.52V7.47a4.85 4.85 0 0 1-1.01-.78z" />
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/company/gentlemansride"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-white transition-colors"
                  aria-label="LinkedIn"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              </div>
            </div>

            <div className="flex flex-col items-center">
              <h3 className="text-[10px] uppercase tracking-widest text-white/40 text-center mb-5">
                {t('getTheApp')}
              </h3>
              <div className="flex items-center gap-3 h-10">
                <a
                  href="https://apps.apple.com/us/app/distinguished-gentlemans-ride/id6475795204"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/5 px-3 py-2 text-white hover:bg-white/10 transition-colors"
                  aria-label="Download on the App Store"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <div className="leading-tight text-left">
                    <div className="text-[8px] uppercase tracking-wider text-white/60">
                      Download on the
                    </div>
                    <div className="text-sm font-semibold">App Store</div>
                  </div>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.gentlemansride.dgr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/5 px-3 py-2 text-white hover:bg-white/10 transition-colors"
                  aria-label="Get it on Google Play"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24">
                    <path
                      d="M3.4 1.6c-.3.2-.4.6-.4 1v18.8c0 .4.2.7.4.9l10.6-10.6L3.4 1.6z"
                      fill="#00d4ff"
                    />
                    <path
                      d="M17.7 8.6 4.6 1.2c-.4-.2-.7-.2-1 0l10.6 10.5 3.5-3.1z"
                      fill="#00f176"
                    />
                    <path d="m17.7 15.4-3.5-3.1L3.6 22.8c.3.2.6.2 1 0l13.1-7.4z" fill="#ff3a44" />
                    <path d="m21 11-3.3-1.9-3.7 2.9 3.7 2.9 3.3-1.9c1-.6 1-2 0-2z" fill="#ffce00" />
                  </svg>
                  <div className="leading-tight text-left">
                    <div className="text-[8px] uppercase tracking-wider text-white/60">
                      Get it on
                    </div>
                    <div className="text-sm font-semibold">Google Play</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Grid */}
      <div className="container py-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-4">
          {sections.map((section) => (
            <div key={section.title}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-3">
                {section.title}
              </h3>
              <ul className="space-y-1.5">
                {section.links.map((link) => (
                  <li key={link.href}>
                    {link.href === '#cookie-settings' ? (
                      <button
                        type="button"
                        onClick={resetCookieConsent}
                        className="text-sm text-white/70 hover:text-white transition-colors"
                      >
                        {link.label}
                      </button>
                    ) : (
                      <Link
                        prefetch={false}
                        href={link.href}
                        target={link.target}
                        rel={link.rel}
                        className="text-sm text-white/70 hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container py-5">
          <div className="flex items-center justify-center gap-4">
            <Image
              src="/images/dgr/DGRLogo.svg"
              alt={tCommon('brandAlt')}
              width={80}
              height={34}
              className="h-7 w-auto brightness-0 invert opacity-60"
            />
            <span className="text-xs text-white/40">
              {copyright || t('copyright', { year: currentYear, tenantName })}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
